<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div v-if="isEditing" class="editable-button-cell-content-wrapper">
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                    contenteditable="true"
                >
                    {{ valueFormatted }}
                </div>
                <div
                    class="editable-button-cell-icon-container"
                    @mousedown.prevent="openDialog"
                    v-if="hasSlot"
                >
                    <i class="ikon med-zoom fw-bold"></i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-button-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{ 'md-editable-grid-cell-non-editable': !editableCell }"
                    @dblclick="requestEditHandler"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    <slot
                        :name="`display-${columnKey}`"
                        :cell="{
                            item: item,
                            column: columnKey,
                            row: rowKey,
                            value: localValue
                        }"
                    >
                        {{ valueFormatted }}
                    </slot>
                </span>

                <div
                    v-if="hasSlot && editableCell"
                    class="editable-button-cell-display-icon"
                    @mousedown.prevent="requestEditHandler"
                >
                    <i class="ikon med-zoom fw-bold "></i>
                </div>
            </div>
        </div>

        <slot
            :name="`cell-${columnKey}`"
            :cell="{
                item: item,
                column: columnKey,
                row: rowKey,
                value: localValue
            }"
            :visible.sync="showDialog"
            :update="updateValue"
            :close="closeDialog"
        >
        </slot>
    </td>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import EditableCellBase from './EditableCellBase.vue';

export default {
    name: 'EditableButtonCell',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    props: {
        value: [String, Number, Array, Object]
    },
    data() {
        return {
            showDialog: false,
            localValue: this.value
        };
    },
    computed: {
        hasSlot() {
            //TODO: averiguar como saber si en la vista se definio el template para ese slot
            if (this.$scopedSlots) {
                const keySlots = Object.keys(this.$scopedSlots);
                return keySlots.some(slot => slot === `cell-${this.columnKey}`);
            }

            return false;
        }
    },
    watch: {
        value(newValue) {
            this.localValue = newValue;
        }
    },
    methods: {
        startEdit() {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            this.inputContentFocus();
        },
        finishEdit() {
            if (this.isEditing && !this.showDialog) {
                this.isEditing = false;
                const changed =
                    JSON.stringify(this.localValue).trim() !== JSON.stringify(this.value).trim();

                const valid = this.validateCell(this.localValue);
                if (changed) {
                    if (this.isAppendSection) {
                        this.$emit('input', this.localValue);
                    } else {
                        this.$parentInstance.cellEditComplete(
                            this.createEventData(this.localValue)
                        );
                    }
                }

                if (!valid) {
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.invalidMessage(this.name),
                        'errorAutoHide'
                    );
                }
            }
        },
        openDialog() {
            this.showDialog = true;
        },
        closeDialog() {
            this.showDialog = false;
            this.inputContentFocus();
        },
        updateValue(newVal) {
            this.localValue = newVal;
            const el = this.$refs.inputRef;
            el.innerText = this.formatter
                ? this.formatter(this.localValue)
                : JSON.stringify(this.localValue).trim();
            this.closeDialog();
        },
        handleKeydown(event) {
            const allowedKeys = [
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Tab',
                'Enter',
                'Escape'
            ];
            if (allowedKeys.includes(event.key)) {
                return EditableCellBase.methods.handleKeydown.call(this, event);
            }

            event.preventDefault();
        },
        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            if (this.editableCell) {
                this.$nextTick(() => {
                    this.openDialog();
                });
            }
        }
    }
};
</script>

<style scoped>
.editable-button-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}
.editable-button-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-button-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

.editable-button-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-button-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.editable-button-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-button-cell-display-icon {
    color: #19bee8;
}
</style>
